/**
 * Conversões entre o cropper (ngx-image-cropper 6) e o recorte relativo
 * que o servidor guarda (`imagesOrder[].crop`: x, y, w, h em 0..1 sobre o
 * master já rodado, mais `rotation` em graus no sentido horário).
 *
 * Partilhado pelos croppers de imóveis e de empreendimentos.
 */

export type CropRotation = 0 | 90 | 180 | 270;

export interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: CropRotation;
  /** Região inteira numa tela 16:9 (laterais preenchidas), em vez de recortada. */
  fit?: boolean;
  /** Com `fit`: laterais a branco em vez da foto desfocada. */
  white?: boolean;
}

/** Como a publicada chega ao 16:9. */
export type CropFillMode = "crop" | "blur" | "white";

export interface CropperPosition {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** Lado maior da publicada no servidor; define o teto de ampliação. */
export const PUBLISHED_MAX_SIDE = 1500;

/** A publicada é sempre 16:9 (padronização das fotos). */
export const PUBLISHED_ASPECT = 16 / 9;

/**
 * Espelho de `AUTO_CROP_MAX_LOSS`/`shouldFitWhole` do servidor
 * (`image-crop.util.ts`): o automático recorta ao centro até 30% de perda
 * (4:3 perde 25%); acima vai inteira com fundo desfocado. O card de uma
 * imagem nova e o modo inicial do cropper seguem a mesma regra.
 */
export const AUTO_CROP_MAX_LOSS = 0.3;

export function cropLoss(
  width: number | null | undefined,
  height: number | null | undefined,
): number {
  if (!width || !height || width <= 0 || height <= 0) return 0;
  const ratio = width / height;
  return 1 - Math.min(ratio, PUBLISHED_ASPECT) / Math.max(ratio, PUBLISHED_ASPECT);
}

export function shouldFitWhole(
  width: number | null | undefined,
  height: number | null | undefined,
): boolean {
  return cropLoss(width, height) > AUTO_CROP_MAX_LOSS;
}

/** `cropMode` guardado → modo do cropper. */
export function fillModeFromCropMode(mode: string | null | undefined): CropFillMode {
  if (mode === "fill") return "white";
  if (mode === "fit" || mode === "autofit") return "blur";
  return "crop";
}

/**
 * Pré-visualização 16:9 do modo imagem inteira, como o servidor a publica:
 * a imagem centrada, com as laterais preenchidas por ela própria ampliada,
 * desfocada e escurecida (`blur`) ou a branco (`white`). O desfoque é feito
 * desenhando-a numa tela minúscula e voltando a ampliá-la (o `ctx.filter`
 * não existe no Safari).
 */
export function drawFitPreview(
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  mode: "blur" | "white" = "blur",
  outWidth = 500,
): HTMLCanvasElement {
  const outHeight = Math.round(outWidth / PUBLISHED_ASPECT);
  const canvas = document.createElement("canvas");
  canvas.width = outWidth;
  canvas.height = outHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx || !sourceWidth || !sourceHeight) return canvas;

  if (mode === "white") {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, outWidth, outHeight);
  } else {
    const tiny = document.createElement("canvas");
    tiny.width = 32;
    tiny.height = 18;
    const tinyCtx = tiny.getContext("2d");
    if (tinyCtx) {
      const cover = Math.max(tiny.width / sourceWidth, tiny.height / sourceHeight);
      const cw = sourceWidth * cover;
      const ch = sourceHeight * cover;
      tinyCtx.drawImage(source, (tiny.width - cw) / 2, (tiny.height - ch) / 2, cw, ch);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(tiny, 0, 0, outWidth, outHeight);
      ctx.fillStyle = "rgba(0, 0, 0, 0.15)";
      ctx.fillRect(0, 0, outWidth, outHeight);
    }
  }

  const scale = Math.min(outWidth / sourceWidth, outHeight / sourceHeight);
  const fw = sourceWidth * scale;
  const fh = sourceHeight * scale;
  ctx.drawImage(source, (outWidth - fw) / 2, (outHeight - fh) / 2, fw, fh);
  return canvas;
}

/** Carrega uma imagem (data URL, object URL ou Blob) num `HTMLImageElement`. */
export function loadImageElement(source: string | Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = typeof source === "string" ? source : URL.createObjectURL(source);
    const img = new Image();
    img.onload = () => {
      if (typeof source !== "string") URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      if (typeof source !== "string") URL.revokeObjectURL(url);
      reject(new Error("Não foi possível ler a imagem"));
    };
    img.src = url;
  });
}

/**
 * Ampliação máxima sem a publicada ser aumentada artificialmente:
 * master 2000 px → 1,33×; master ≤ 1500 px → 1×.
 */
export function zoomCap(masterWidth: number | null | undefined): number {
  if (!masterWidth || masterWidth <= 0) return 1;
  return Math.max(1, masterWidth / PUBLISHED_MAX_SIDE);
}

export function rotatedSize(
  width: number,
  height: number,
  rotation: CropRotation,
): { width: number; height: number } {
  return rotation === 90 || rotation === 270
    ? { width: height, height: width }
    : { width, height };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * `imagePosition` do cropper → recorte relativo. O cropper devolve a
 * posição da moldura em píxeis da imagem rodada **sem** contar com o zoom
 * (`transform.scale`): com zoom `s` a região realmente desenhada é
 * `(x1 + (s−1)·W/2) / s` com largura `(x2 − x1) / s` (ver `CropService.crop`).
 */
export function toRelativeCrop(
  position: CropperPosition,
  imageWidth: number,
  imageHeight: number,
  rotation: CropRotation,
  scale = 1,
): CropRect {
  const s = scale > 0 ? scale : 1;
  const w = (position.x2 - position.x1) / s;
  const h = (position.y2 - position.y1) / s;
  const x = (position.x1 + ((s - 1) * imageWidth) / 2) / s;
  const y = (position.y1 + ((s - 1) * imageHeight) / 2) / s;

  const rx = clamp(x / imageWidth, 0, 1);
  const ry = clamp(y / imageHeight, 0, 1);
  const rw = clamp(w / imageWidth, 0, 1 - rx);
  const rh = clamp(h / imageHeight, 0, 1 - ry);
  return { x: rx, y: ry, w: rw, h: rh, rotation };
}

/**
 * Recorte relativo → posição inicial da moldura, em píxeis da imagem tal
 * como está exibida (`cropperReady` dá essas dimensões). Só para zoom 1.
 */
export function toCropperPosition(
  crop: CropRect,
  displayed: { width: number; height: number },
): CropperPosition {
  return {
    x1: Math.round(crop.x * displayed.width),
    y1: Math.round(crop.y * displayed.height),
    x2: Math.round((crop.x + crop.w) * displayed.width),
    y2: Math.round((crop.y + crop.h) * displayed.height),
  };
}

export function normalizeRotation(value: number | null | undefined): CropRotation {
  const n = ((Math.round(Number(value) || 0) % 360) + 360) % 360;
  return (n === 90 || n === 180 || n === 270 ? n : 0) as CropRotation;
}
